import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Paper,
  Button,
  LinearProgress,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';

interface Batch {
  batch_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'mixed';
  total_items: number;
  completed_items: number;
  failed_items: number;
  processing_items: number;
  pending_items: number;
  progress_percent: number;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed': return 'success';
    case 'processing': return 'warning';
    case 'failed': return 'error';
    case 'pending': return 'default';
    default: return 'default';
  }
};

const formatDuration = (seconds: number | null) => {
  if (seconds === null) return '—';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

export default function ValidationBatches() {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/validation/batches/list?limit=200');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setBatches(data.batches || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
    const interval = setInterval(fetchBatches, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const handleBatchClick = (batch: Batch) => {
    setSelectedBatch(batch);
    setDialogOpen(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedBatches = batches.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Batch History
        </Typography>
        <Button variant="outlined" size="small" onClick={fetchBatches}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && batches.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Batch ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Completed</TableCell>
                <TableCell align="right">Failed</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBatches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No batches found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedBatches.map((batch) => (
                  <TableRow
                    key={batch.batch_id}
                    hover
                    onClick={() => handleBatchClick(batch)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {batch.batch_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={batch.status} size="small" color={getStatusColor(batch.status)} />
                    </TableCell>
                    <TableCell align="right">{batch.total_items}</TableCell>
                    <TableCell align="right">
                      <Typography color="success.main" fontWeight="600">
                        {batch.completed_items}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="error.main" fontWeight="600">
                        {batch.failed_items}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={batch.progress_percent}
                          sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {batch.progress_percent}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {batch.started_at ? new Date(batch.started_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>{formatDuration(batch.duration_seconds)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={batches.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Batch Details Dialog */}
      {selectedBatch && (
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Batch Details: {selectedBatch.batch_id}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <GridStats batch={selectedBatch} />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

function GridStats({ batch }: { batch: Batch }) {
  return (
    <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight="bold">{batch.total_items}</Typography>
        <Typography variant="body2" color="text.secondary">Total Items</Typography>
      </Paper>
      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
        <Typography variant="h6" fontWeight="bold">{batch.completed_items}</Typography>
        <Typography variant="body2">Completed</Typography>
      </Paper>
      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
        <Typography variant="h6" fontWeight="bold">{batch.failed_items}</Typography>
        <Typography variant="body2">Failed</Typography>
      </Paper>
    </Box>
  );
}
